import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import EntityType from "@mapmycustomers/shared/enum/EntityType";
import {
  EntitiesSupportingLeadGen,
  EntityTypesSupportingRoutes,
  EntityTypeSupportingLeadGen,
  Lead,
} from "@mapmycustomers/shared/types/entity";

import { setAddRecordsVisibility, submitAddRecords } from "@app/component/leadFinder/store/actions";
import { getAddRecordsEntityType } from "@app/component/leadFinder/store/selectors";
import { MultiStepWizard } from "@app/component/wizard";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { getEntityTypeAnalyticsName } from "@app/util/ui";

import styles from "./AddRecordsWizard.module.scss";
import AddRecordsWizardState from "./AddRecordsWizardState";
import steps from "./steps";

const DEFAULT_ALLOWED_ENTITY_TYPES: EntityTypeSupportingLeadGen[] = [
  EntityType.COMPANY,
  EntityType.PERSON,
];

interface Props {
  allowedEntityTypes?: EntityTypeSupportingLeadGen[];
  entityType: EntityTypesSupportingRoutes;
  includeRoutesStep?: boolean;
  onClose?: () => void;
  onComplete?: () => void;
  onRecordsCreated?: (
    entityIds: Array<EntitiesSupportingLeadGen["id"]>,
    entityType: EntityTypeSupportingLeadGen
  ) => void;
  selectedLeads: Lead[];
  setAddRecordsVisibility: typeof setAddRecordsVisibility;
  submitAddRecords: typeof submitAddRecords.request;
}

const AddRecordsWizard = ({
  allowedEntityTypes = DEFAULT_ALLOWED_ENTITY_TYPES,
  entityType,
  includeRoutesStep = true,
  onClose,
  onComplete,
  onRecordsCreated,
  selectedLeads,
  setAddRecordsVisibility,
  submitAddRecords,
}: Props) => {
  const analytics = useAnalytics();

  const initialState: AddRecordsWizardState = useMemo(
    () => ({
      allowedEntityTypes,
      entityType,
      includeRoutesStep,
      selectedLeads,
    }),
    [allowedEntityTypes, entityType, includeRoutesStep, selectedLeads]
  );

  const handleHide = useCallback(
    (finished: boolean) => {
      if (!finished) {
        setAddRecordsVisibility({ visible: false });
      }
    },
    [setAddRecordsVisibility]
  );

  const handleFinish = useCallback(
    ({
      customFields,
      entity,
      entityType,
      groupIds,
      layout,
      personId,
      routeIdsOrRoutePayloads,
      selectedLeads,
    }: AddRecordsWizardState) => {
      submitAddRecords({
        customFields,
        entity: entity!,
        entityType,
        groupIds,
        layoutId: layout!.id,
        onClose,
        onComplete,
        onRecordsCreated,
        personId,
        routeIdsOrRoutePayloads,
        selectedLeads,
      });

      analytics.completed("", {
        entityType: getEntityTypeAnalyticsName(entityType, false, true),
      });
    },
    [analytics, onClose, onComplete, onRecordsCreated, submitAddRecords]
  );

  return (
    <MultiStepWizard
      className={styles.container}
      initialState={initialState}
      onFinish={handleFinish}
      onHide={handleHide}
      steps={steps}
      width="auto"
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  entityType: getAddRecordsEntityType(state),
});

const mapDispatchToProps = {
  setAddRecordsVisibility,
  submitAddRecords: submitAddRecords.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(AddRecordsWizard);
